'use client';

import type { ChangeEvent } from 'react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import Backdrop from '@/components/shared/Backdrop';
import SearchBar from '@/components/shared/SearchBar';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useLockBodyScroll } from '@/hooks/useLockBodyScroll';
import { useScrollToTop } from '@/hooks/useScrollToTop';
import { useUrlSearch } from '@/hooks/useUrlSearch';
import { cn } from '@/utils/cn';
import { normalizeSearchQuery } from '@/utils/texts.utils';
import GamesFiltered from './components/GamesFiltered';

export default function SearchGames() {
  const t = useTranslations('lobby.filters');
  const [isFocused, setIsFocused] = useState(false);

  const isMobile = useIsMobile();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const { searchValue, setSearchValue, searchParam } = useUrlSearch({ searchKey: 'search', delay: isMobile ? 1500 : 500 });

  const inputValue = normalizeSearchQuery(searchValue);
  const queryValue = normalizeSearchQuery(searchParam);

  const isDropdownOpen = isFocused || Boolean(inputValue);

  const scrollSearchToTopOnMobile = useScrollToTop({
    searchInputRef,
    isMobile,
    topGap: 68,
    onlyWhenInputFocused: true,
    enabled: isDropdownOpen,
    alignOnKeyboardOpen: true,
    keyboardThresholdPx: 68,
  });

  useLockBodyScroll(isDropdownOpen && isMobile);

  useEffect(() => {
    if (isMobile && inputValue) {
      scrollSearchToTopOnMobile();
    }
  }, [isMobile, inputValue, scrollSearchToTopOnMobile]);

  const clearSearch = () => {
    setSearchValue('');
  };

  const blurSearchInput = () =>
    searchInputRef.current?.blur();

  const handleClose = () => {
    setIsFocused(false);
    clearSearch();
  };

  const handleSearchFocus = useCallback(() => {
    scrollSearchToTopOnMobile();
    setIsFocused(true);
  }, [scrollSearchToTopOnMobile]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
  };

  useClickOutside(searchContainerRef, handleClose, { enabled: !isMobile && isDropdownOpen });

  return (
    <div onFocus={() => setIsFocused(true)} className="relative w-full mb-4.5 lg:mb-6.5 3xl:mb-8.5">
      <div ref={searchContainerRef} className={cn('relative', isDropdownOpen && isMobile ? 'z-50' : '')}>
        <SearchBar
          ref={searchInputRef}
          placeholder={t('search-placeholder')}
          query={searchValue}
          onChange={handleSearch}
          onFocus={handleSearchFocus}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isMobile) {
              e.currentTarget.blur();
            }
          }}
          clearSearch={clearSearch}
        />

        {isDropdownOpen && isMobile && <Backdrop onClick={handleClose} />}
        <GamesFiltered
          searchValue={queryValue}
          inputValue={inputValue}
          isFocused={isDropdownOpen}
          blurSearchInput={blurSearchInput}
        />
      </div>
    </div>
  );
}
